import React from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';

interface ErrorPageProps {
  code?: number;
  title?: string;
  message?: string;
  onRetry?: () => void;
  onNavigateHome?: () => void;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({
  code = 500,
  title,
  message,
  onRetry,
  onNavigateHome
}) => {
  const { t } = useTranslation();

  const handleGoHome = () => {
    if (onNavigateHome) {
      onNavigateHome();
    } else {
      // Fallback to window.location if no navigate function provided
      window.location.href = '/dashboard';
    }
  };

  const defaultContent = {
    404: {
      title: t('errors.pageNotFound'),
      message: t('errors.pageNotFoundDesc')
    },
    403: {
      title: t('errors.accessDenied'),
      message: t('errors.accessDeniedDesc')
    },
    500: {
      title: t('errors.somethingWrong'),
      message: t('errors.somethingWrongDesc')
    }
  };

  const content = defaultContent[code as keyof typeof defaultContent] || defaultContent[500];
  const displayTitle = title || content.title;
  const displayMessage = message || content.message;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md">
        <div className="flex justify-center mb-6">
          <div className="bg-red-100 p-4 rounded-full">
            <AlertTriangle className="h-12 w-12 text-red-600" />
          </div>
        </div>

        <h1 className="text-4xl font-bold text-gray-900 mb-2">{code}</h1>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">{displayTitle}</h2>
        <p className="text-gray-600 mb-8">{displayMessage}</p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            onClick={handleGoHome}
          >
            <Home className="mr-2 h-4 w-4" />
            {t('errors.goToDashboard')}
          </Button>

          {onRetry && (
            <Button onClick={onRetry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common.tryAgain')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
